import { useList } from '@refinedev/core'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid, Area, AreaChart,
} from 'recharts'
import { flaskApi } from '@/lib/flaskApi'
import { cn } from '@/lib/utils'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

const BRAND = '#076698'
const BRAND_PALETTE = ['#076698', '#0e8fc7', '#04a2d4', '#90cdff', '#c8e8f5']
const TOOLTIP_STYLE = {
  borderRadius: '14px',
  border: 'none',
  boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
  fontSize: 12,
  padding: '8px 14px',
}
const AXIS_TICK = { fontSize: 11, fill: 'var(--muted-foreground)' }

interface ChartCardProps {
  title: string
  subtitle?: string
  children: React.ReactNode
  className?: string
  badge?: React.ReactNode
}

function ChartCard({ title, subtitle, children, className, badge }: ChartCardProps) {
  return (
    <div className={cn('bento-card p-6 flex flex-col gap-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-extrabold text-slate-700 dark:text-slate-200 tracking-tight leading-tight">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {badge}
      </div>
      {children}
    </div>
  )
}

interface YoYBadgeProps { current: number; previous: number }
function YoYBadge({ current, previous }: YoYBadgeProps) {
  if (!previous) return null
  const delta = ((current - previous) / previous) * 100
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus
  const color = delta > 0
    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30'
    : delta < 0
      ? 'text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/30'
      : 'text-slate-500 bg-slate-100 dark:bg-slate-800'
  return (
    <span className={cn('flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full shrink-0', color)}>
      <Icon size={10} />
      {delta > 0 ? '+' : ''}{delta.toFixed(0)}% YoY
    </span>
  )
}


export function InterviewTypeChart() {
  const { data } = useList({ resource: 'interview_versions', pagination: { pageSize: 1000 } })
  const counts = ((data?.data ?? []) as Record<string, unknown>[]).reduce<Record<string, number>>(
    (acc, r) => {
      const type = (r.type as string) ?? 'unknown'
      acc[type] = (acc[type] ?? 0) + 1
      return acc
    },
    {}
  )
  const chartData = Object.entries(counts).map(([name, count]) => ({ name, count }))

  return (
    <ChartCard title="Interviews by Type" subtitle="All time, by type">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} barCategoryGap="35%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(7,102,152,0.06)' }} />
          <Bar dataKey="count" fill={BRAND} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function SubscriptionStatusChart() {
  const { data } = useList({ resource: 'subscriptions', pagination: { pageSize: 1000 } })
  const counts = ((data?.data ?? []) as Record<string, unknown>[]).reduce<{ active: number; inactive: number }>(
    (acc, r) => { r.active ? acc.active++ : acc.inactive++; return acc },
    { active: 0, inactive: 0 }
  )
  const chartData = [
    { name: 'Active', value: counts.active },
    { name: 'Inactive', value: counts.inactive },
  ]

  return (
    <ChartCard title="Subscription Status" subtitle="Active vs inactive">
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%"
            outerRadius={72} innerRadius={30} paddingAngle={3} label>
            {chartData.map((_, i) => <Cell key={i} fill={BRAND_PALETTE[i]} />)}
          </Pie>
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function TopCompaniesByInterviewsChart() {
  const { data } = useQuery({
    queryKey: ['dashboard', 'top-companies-by-interviews'],
    queryFn: () =>
      flaskApi.get<{ companyName: string; interviewCount: number }[]>(
        '/dashboard/top-companies-by-interviews'
      ).then((r) => r.data),
  })
  const chartData = (data ?? []).map((d) => ({ name: d.companyName, count: d.interviewCount }))

  return (
    <ChartCard title="Top Companies by Interviews" subtitle="Top 5 most active">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} layout="vertical" barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" horizontal={false} />
          <XAxis type="number" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" tick={{ ...AXIS_TICK, fontSize: 10 }} width={100} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(7,102,152,0.06)' }} />
          <Bar dataKey="count" fill={BRAND} radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function TopCompaniesByUsersChart() {
  const { data } = useQuery({
    queryKey: ['dashboard', 'top-companies-by-users'],
    queryFn: () =>
      flaskApi.get<{ companyName: string; userCount: number }[]>(
        '/dashboard/top-companies-by-users'
      ).then((r) => r.data),
  })
  const chartData = (data ?? []).map((d) => ({ name: d.companyName, count: d.userCount }))

  return (
    <ChartCard title="Top Companies by Users" subtitle="Top 5 by user count">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} layout="vertical" barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" horizontal={false} />
          <XAxis type="number" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" tick={{ ...AXIS_TICK, fontSize: 10 }} width={100} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(7,102,152,0.06)' }} />
          <Bar dataKey="count" fill="#0e8fc7" radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function TopAgenciesByOrgsChart() {
  const { data } = useQuery({
    queryKey: ['dashboard', 'top-agencies-by-orgs'],
    queryFn: () =>
      flaskApi.get<{ name: string; orgCount: number }[]>(
        '/dashboard/top-agencies-by-orgs'
      ).then((r) => r.data),
  })
  const chartData = (data ?? []).map((d) => ({ name: d.name, count: d.orgCount }))

  return (
    <ChartCard title="Top Agencies by Organizations" subtitle="Top 5 agencies">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} layout="vertical" barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" horizontal={false} />
          <XAxis type="number" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" tick={{ ...AXIS_TICK, fontSize: 10 }} width={100} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(7,102,152,0.06)' }} />
          <Bar dataKey="count" fill="#044268" radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function CompaniesWithSubscriptionChart() {
  const { data } = useQuery({
    queryKey: ['dashboard', 'companies-subscription-ratio'],
    queryFn: () =>
      flaskApi.get<{ withSubscription: number; withoutSubscription: number }>(
        '/dashboard/companies-subscription-ratio'
      ).then((r) => r.data),
  })
  const total = (data?.withSubscription ?? 0) + (data?.withoutSubscription ?? 0)
  const pct = total > 0 ? Math.round(((data?.withSubscription ?? 0) / total) * 100) : 0
  const chartData = [
    { name: 'Subscribed', value: data?.withSubscription ?? 0 },
    { name: 'Free', value: data?.withoutSubscription ?? 0 },
  ]

  return (
    <ChartCard
      title="Subscription Coverage"
      subtitle="Companies with active plans"
      badge={
        <span className="text-[10px] font-extrabold px-2 py-1 rounded-full bg-[#076698]/10 text-[#076698] dark:bg-sky-900/40 dark:text-sky-300 shrink-0">
          {pct}% covered
        </span>
      }
    >
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%"
            outerRadius={72} innerRadius={30} paddingAngle={3} label>
            {chartData.map((_, i) => <Cell key={i} fill={BRAND_PALETTE[i]} />)}
          </Pie>
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}


interface GrowthRow {
  year: number
  companies: number
  users: number
  applications: number
  interviews: number
  subscriptions: number
}

function useGrowthData() {
  return useQuery({
    queryKey: ['dashboard', 'growth', 'combined'],
    queryFn: () =>
      flaskApi.get<GrowthRow[]>('/dashboard/growth/combined').then((r) => r.data),
  })
}

function currentAndPrevious(data: GrowthRow[] | undefined, key: keyof GrowthRow) {
  if (!data || data.length === 0) return { current: 0, previous: 0 }
  const current = Number(data[data.length - 1][key])
  const previous = data.length > 1 ? Number(data[data.length - 2][key]) : 0
  return { current, previous }
}

export function CompaniesGrowthChart() {
  const { data } = useGrowthData()
  const chartData = (data ?? []).map((r) => ({ year: String(r.year), count: r.companies }))
  const { current, previous } = currentAndPrevious(data, 'companies')

  return (
    <ChartCard
      title="Company Growth"
      subtitle="New companies registered per year"
      badge={<YoYBadge current={current} previous={previous} />}
    >
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="gradCompanies" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={BRAND} stopOpacity={0.18} />
              <stop offset="95%" stopColor={BRAND} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="year" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Area type="monotone" dataKey="count" stroke={BRAND} strokeWidth={2.5}
            fill="url(#gradCompanies)" dot={{ fill: BRAND, r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function UsersGrowthChart() {
  const { data } = useGrowthData()
  const chartData = (data ?? []).map((r) => ({ year: String(r.year), count: r.users }))
  const { current, previous } = currentAndPrevious(data, 'users')

  return (
    <ChartCard
      title="User Growth"
      subtitle="New user accounts per year"
      badge={<YoYBadge current={current} previous={previous} />}
    >
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0e8fc7" stopOpacity={0.18} />
              <stop offset="95%" stopColor="#0e8fc7" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="year" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Area type="monotone" dataKey="count" stroke="#0e8fc7" strokeWidth={2.5}
            fill="url(#gradUsers)" dot={{ fill: '#0e8fc7', r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function ApplicationsGrowthChart() {
  const { data } = useGrowthData()
  const chartData = (data ?? []).map((r) => ({ year: String(r.year), count: r.applications }))
  const { current, previous } = currentAndPrevious(data, 'applications')

  return (
    <ChartCard
      title="Application Volume"
      subtitle="Candidate applications per year"
      badge={<YoYBadge current={current} previous={previous} />}
    >
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="gradApps" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#04a2d4" stopOpacity={0.18} />
              <stop offset="95%" stopColor="#04a2d4" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="year" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Area type="monotone" dataKey="count" stroke="#04a2d4" strokeWidth={2.5}
            fill="url(#gradApps)" dot={{ fill: '#04a2d4', r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function InterviewsGrowthChart() {
  const { data } = useGrowthData()
  const chartData = (data ?? []).map((r) => ({ year: String(r.year), count: r.interviews }))
  const { current, previous } = currentAndPrevious(data, 'interviews')

  return (
    <ChartCard
      title="Interview Volume"
      subtitle="Interview sessions per year"
      badge={<YoYBadge current={current} previous={previous} />}
    >
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="gradInterviews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#044268" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#044268" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="year" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Area type="monotone" dataKey="count" stroke="#044268" strokeWidth={2.5}
            fill="url(#gradInterviews)" dot={{ fill: '#044268', r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function SubscriptionsGrowthChart() {
  const { data } = useGrowthData()
  const chartData = (data ?? []).map((r) => ({ year: String(r.year), count: r.subscriptions }))
  const { current, previous } = currentAndPrevious(data, 'subscriptions')

  return (
    <ChartCard
      title="Subscription Growth"
      subtitle="New subscriptions per year"
      badge={<YoYBadge current={current} previous={previous} />}
    >
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="gradSubs" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#90cdff" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#90cdff" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="year" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Area type="monotone" dataKey="count" stroke="#90cdff" strokeWidth={2.5}
            fill="url(#gradSubs)" dot={{ fill: '#90cdff', r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export function CandidatesGrowthChart() {
  const { data } = useList({
    resource: 'local_candidates',
    pagination: { pageSize: 5000 },
  })

  const records = (data?.data ?? []) as Record<string, unknown>[]
  const byYear = records.reduce<Record<string, number>>((acc, r) => {
    const raw = r.created_at as string | undefined
    if (!raw) return acc
    const year = new Date(raw).getFullYear().toString()
    acc[year] = (acc[year] ?? 0) + 1
    return acc
  }, {})

  const chartData = Object.entries(byYear).sort(([a], [b]) => Number(a) - Number(b))
    .map(([year, count]) => ({ year, count }))

  const current = chartData.length > 0 ? chartData[chartData.length - 1].count : 0
  const previous = chartData.length > 1 ? chartData[chartData.length - 2].count : 0

  return (
    <ChartCard
      title="Candidate Growth"
      subtitle="New candidates registered per year"
      badge={<YoYBadge current={current} previous={previous} />}
    >
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="gradCandidates" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.18} />
              <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="year" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Area type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2.5}
            fill="url(#gradCandidates)" dot={{ fill: '#7c3aed', r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}


export function PlatformGrowthOverviewChart() {
  const { data } = useGrowthData()
  const chartData = (data ?? []).map((r) => ({
    year: String(r.year),
    Companies: r.companies,
    Users: r.users,
    Applications: r.applications,
    Interviews: r.interviews,
  }))

  return (
    <ChartCard title="Platform Growth Overview" subtitle="All key metrics by year" className="col-span-full">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
          <XAxis dataKey="year" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="Companies" stroke="#076698" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="Users" stroke="#0e8fc7" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="Applications" stroke="#04a2d4" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="Interviews" stroke="#044268" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}
